import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { makeStyles, Theme, Typography } from '@material-ui/core'
import React, { FC, Fragment, useMemo } from 'react'

import Option from './Option'

import { ManufacturerProductPropertyOptionDetail } from '@deltek/specification-client/core/Models/product'
import { SupportedOptionsObject } from '../ListingDetailsPage'
import { MANUFACTURER_OPTION, MASTERSPEC_OPTION } from './PremiumPlus/constants'
import MSPremiumProperties from './PremiumPlus/MSPremiumProperties'

const useStyles = makeStyles((theme: Theme) => ({
    propPrefixPostfix: {
        fontSize: pxToRem(13),
        lineHeight: '1.7',
        display: 'inline-block',
        minWidth: 'fit-content',
        marginBottom: theme.spacing(1),
        marginRight: theme.spacing(2),
        verticalAlign: 'bottom',
    },
}))

interface OptionGroupsProps {
    groupId?: string
    prefix: string
    options: Array<Partial<ManufacturerProductPropertyOptionDetail>>
    propertyName: string
    subPropertyName: string
    postfix: string
    selectedData: SupportedOptionsObject
    mode?: string
    propertyId?: string
}

const OptionGroups: FC<OptionGroupsProps> = ({
    prefix,
    options,
    propertyName,
    subPropertyName,
    postfix,
    mode,
    selectedData,
    groupId = '',
    propertyId = '',
}) => {
    const classes = useStyles()

    const groupOptions = useMemo(
        () =>
            mode === 'nonMSviewOnly'
                ? options
                : mode === 'premPlus'
                ? options?.filter((option) => !option.isPremiumPlus)
                : options?.filter(
                      (option) =>
                          (option.isPremiumPlus && option.isSupported) ||
                          !option.isPremiumPlus ||
                          (!!option.isPremiumPlus &&
                              subPropertyName &&
                              option.optionId &&
                              selectedData[propertyName][
                                  subPropertyName
                              ].includes(option.optionId))
                  ),
        [mode, options]
    )

    return (
        <>
            {prefix && (
                <Typography
                    component="span"
                    className={classes.propPrefixPostfix}
                >
                    {prefix}
                </Typography>
            )}
            {groupOptions.map(
                ({
                    isOther,
                    optionId = '',
                    value = '',
                    isPremiumPlus = false,
                }) => {
                    const isSelected =
                        !!value &&
                        !!selectedData &&
                        selectedData[propertyName] &&
                        selectedData[propertyName][subPropertyName].includes(
                            optionId
                        )

                    const tooltipText = isPremiumPlus
                        ? isSelected
                            ? `${MANUFACTURER_OPTION} selected`
                            : MANUFACTURER_OPTION
                        : isSelected
                        ? `${MASTERSPEC_OPTION} selected`
                        : MASTERSPEC_OPTION

                    const isManufacturerOptionsDisabled: boolean =
                        mode === 'edit' && isPremiumPlus // disable Manufacturer options in listing detail property section

                    if (
                        (!!isPremiumPlus &&
                            !isSelected &&
                            mode === 'premPlus') ||
                        isOther
                    ) {
                        return <Fragment key={optionId}></Fragment>
                    }

                    return (
                        <Option
                            key={optionId}
                            value={value}
                            optionId={optionId}
                            propertyName={propertyName}
                            subPropertyName={subPropertyName}
                            isSelected={isSelected}
                            mode={mode}
                            isPremiumPlus={isPremiumPlus}
                            tooltipText={tooltipText}
                            disabled={isManufacturerOptionsDisabled}
                        />
                    )
                }
            )}

            {mode === 'premPlus' && (
                <MSPremiumProperties
                    propertyName={propertyName}
                    subPropertyName={subPropertyName}
                    groupId={groupId}
                    propertyId={propertyId}
                />
            )}

            {postfix && (
                <Typography
                    component="span"
                    className={classes.propPrefixPostfix}
                >
                    {postfix}
                </Typography>
            )}
        </>
    )
}

export default OptionGroups
